import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { EventDetailsSummary, EventDetailsParticipantTabs } from './EventDetailsContent';
import ErrorBanner from './ErrorBanner';
import { getMessageForFetchError } from '../shared/utils/apiError';
import ConfirmationModal from "./ConfirmationModal";
import EventParticipantContactRosterModal from "./EventParticipantContactRosterModal";
import { buildEventsManagementPath, EVENT_MGMT_TAB_CLOSE_RECORD, EVENT_MGMT_TAB_EVENTS } from '../shared/utils/eventManagementTabs';
import { volunteerOpportunityRequestBody } from '../shared/api/volunteerOpportunityContext';
import { filterVolunteerOpportunitiesByEventId, toCreateFromEventState } from '../shared/utils/volunteerOpportunityFromEvent';
import { buildVolunteerOpportunitiesPath } from '../shared/utils/volunteerOpportunityNavigation';
import LinkifiedText from './LinkifiedText';
import { fetchCurrentAopProgramNames } from '../shared/api/currentAopByProgram';
import { isProgramNameInCurrentAop } from '../shared/utils/currentAopByProgram';
import {
  buildContactRosterCsvExportText,
  buildContactRosterCsvFilename,
  printContactRoster,
} from '../shared/utils/eventParticipantContactRoster';
import {
  formatKidHours,
  getEventKidHoursEntered,
  getEventKidHoursRecord,
  getEventKidHoursTotal,
} from '../shared/utils/eventKidHours';
import { computeRegistrationMetrics } from '../shared/utils/eventRegistrationMetrics';
import { downloadCsvTextFromString } from '../shared/utils/csvFileHelpers';
import EventRegistrationKpiCards from './EventRegistrationKpiCards';

import { apiFetch } from "../shared/api/apiFetch";

const labelStyle = { color: '#6c757d', marginRight: '8px', minWidth: '140px', display: 'inline-block' };
const uiConfig = require('../uiConfig');

/**
 * Event Details shown in a modal so the parent context remains visible.
 * Used from Events, Event Participants, and Close Event Record. Direct URL /Events/:eventId redirects to Events with modal open.
 */
function EventDetailModal({ eventId, open, onClose, returnTo = buildEventsManagementPath({ tab: EVENT_MGMT_TAB_EVENTS }), eventsFilters }) {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
  const account_id = userInfo?.account_id;
  const user_id = userInfo?._id;
  const role = userInfo?.role;
  const volunteerSession = { account_id, user_id, role };
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const [event, setEvent] = useState(null);
  const [eventLoading, setEventLoading] = useState(true);
  const [participants, setParticipants] = useState([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [volunteerOpportunities, setVolunteerOpportunities] = useState([]);
  const [volunteerLoading, setVolunteerLoading] = useState(false);
  const [tab, setTab] = useState('participants');
  const [selectedParticipantIds, setSelectedParticipantIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const [bannerError, setBannerError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  const [contactRosterOpen, setContactRosterOpen] = useState(false);
  /** null = not loaded yet (treat as AOP until known) */
  const [aopProgramNames, setAopProgramNames] = useState(null);

  const canManage = hasPermission('menu.events');
  const canRecordClose = hasPermission('menu.close_event_record');
  const canViewVolunteer = hasPermission('menu.volunteer_opportunities');
  const eventInCurrentAop = isProgramNameInCurrentAop(event?.program_name, aopProgramNames);

  const loadEvent = useCallback(() => {
    if (!eventId || !account_id) return;
    setEventLoading(true);
    setEvent(null);
    apiFetch(serverUrl + '/getEventsWithDetails', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        const found = list.find((e) => String(e._id) === String(eventId));
        setEvent(found || null);
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)))
      .finally(() => setEventLoading(false));
  }, [eventId, account_id, serverUrl]);

  const loadParticipants = useCallback(() => {
    if (!eventId || !account_id) return Promise.resolve([]);
    setParticipantsLoading(true);
    return apiFetch(serverUrl + '/getEventParticipants', {
      method: 'POST',
      body: JSON.stringify({ account_id, event_id: eventId }),
    })
      .then((resp) => resp.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setParticipants(list);
        return list;
      })
      .catch(() => {
        setParticipants([]);
        return [];
      })
      .finally(() => setParticipantsLoading(false));
  }, [eventId, account_id, serverUrl]);

  const loadVolunteerOpportunities = useCallback(() => {
    const linkedEventId = eventId != null ? String(eventId).trim() : '';
    if (!linkedEventId || !account_id || !canViewVolunteer) return Promise.resolve([]);
    setVolunteerLoading(true);
    return apiFetch(serverUrl + '/listVolunteerOpportunities', {
      method: 'POST',
      body: JSON.stringify(
        volunteerOpportunityRequestBody(volunteerSession, { event_id: linkedEventId })
      ),
    })
      .then((resp) => resp.json())
      .then((data) => {
        const list = filterVolunteerOpportunitiesByEventId(
          Array.isArray(data) ? data : [],
          linkedEventId
        );
        setVolunteerOpportunities(list);
        return list;
      })
      .catch(() => {
        setVolunteerOpportunities([]);
        return [];
      })
      .finally(() => setVolunteerLoading(false));
  }, [eventId, account_id, serverUrl, canViewVolunteer, user_id, role]);

  useEffect(() => {
    if (!open) setContactRosterOpen(false);
  }, [open]);

  useEffect(() => {
    if (open && eventId) loadEvent();
  }, [open, eventId, loadEvent]);

  useEffect(() => {
    if (!open || !account_id) return;
    let cancelled = false;
    fetchCurrentAopProgramNames({ serverUrl, account_id })
      .then((names) => {
        if (!cancelled) setAopProgramNames(names);
      })
      .catch((err) => {
        if (!cancelled) setBannerError(getMessageForFetchError(err, "Failed to load Current AOP programs."));
      });
    return () => {
      cancelled = true;
    };
  }, [open, account_id, serverUrl]);

  useEffect(() => {
    if (!open || !event?._id) return;
    setTab('participants');
    setSelectedParticipantIds(new Set());
    loadParticipants().then((list) => {
      if (Array.isArray(list)) {
        const waitlisted = list.filter((p) => (p.attendance || '').trim() === 'Waitlisted').length;
        const cancelled = list.filter((p) => (p.attendance || '').trim() === 'Cancelled').length;
        const registered = list.filter((p) => {
          const att = (p.attendance || '').trim();
          return att !== 'Waitlisted' && att !== 'Cancelled';
        }).length;
        setEvent((prev) => {
          if (!prev) return prev;
          const next = {
            ...prev,
            participant_count: list.length,
            waitlisted_count: waitlisted,
            cancelled_count: cancelled,
            registered_count: registered,
          };
          return next;
        });
      }
    });
  }, [open, event?._id, loadParticipants]);

  useEffect(() => {
    if (!open || !eventId || !canViewVolunteer) {
      setVolunteerOpportunities([]);
      return;
    }
    loadVolunteerOpportunities();
  }, [open, eventId, canViewVolunteer, loadVolunteerOpportunities]);

  const handleOpenVolunteerOpportunity = (opp) => {
    if (!opp?._id) return;
    onClose();
    navigate(buildVolunteerOpportunitiesPath({ detail: opp._id }));
  };

  const handleCreateVolunteerOpportunity = () => {
    if (!event) return;
    onClose();
    navigate('/VolunteerOpportunities', { state: { createFromEvent: toCreateFromEventState(event) } });
  };

  const handleSelectOne = (id, checked) => {
    setSelectedParticipantIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleSelectAll = (list, checked) => {
    if (!list?.length) return;
    setSelectedParticipantIds((prev) => {
      const next = new Set(prev);
      list.forEach((p) => (checked ? next.add(p._id) : next.delete(p._id)));
      return next;
    });
  };

  const removeOne = (participantId) => {
    setConfirmDialog({
      open: true,
      title: "Remove participant",
      message: "Remove this participant from the event?",
      onConfirm: () => {
        apiFetch(serverUrl + '/deleteEventParticipant', {
          method: 'POST',
          body: JSON.stringify({ account_id, _id: participantId }),
        })
          .then((r) => r.text())
          .then(() => {
            setSelectedParticipantIds((prev) => {
              const next = new Set(prev);
              next.delete(participantId);
              return next;
            });
            loadParticipants().then((list) => {
              if (event && Array.isArray(list)) setEvent((prev) => (prev ? { ...prev, participant_count: list.length } : null));
            });
          })
          .catch((err) => setBannerError(getMessageForFetchError(err)));
      },
    });
  };

  const removeSelected = async () => {
    if (selectedParticipantIds.size === 0) return;
    const ids = Array.from(selectedParticipantIds);
    setConfirmDialog({
      open: true,
      title: "Remove participants",
      message: `Remove ${selectedParticipantIds.size} participant(s) from the event?`,
      onConfirm: async () => {
        setDeleting(true);
        let success = 0;
        let fail = 0;
        for (const _id of ids) {
          try {
            const res = await apiFetch(serverUrl + '/deleteEventParticipant', {
              method: 'POST',
              body: JSON.stringify({ account_id, _id }),
            });
            if (res.ok) success++;
            else fail++;
          } catch {
            fail++;
          }
        }
        setDeleting(false);
        setSelectedParticipantIds(new Set());
        const list = await loadParticipants();
        if (event && Array.isArray(list)) setEvent((prev) => (prev ? { ...prev, participant_count: list.length } : null));
        if (fail > 0) setBannerError(`Deleted ${success}; ${fail} failed.`);
        else if (success > 0) setBannerError('');
      },
    });
  };

  const handleBack = () => onClose();

  const handleContactRosterConfirm = ({ action, rows }) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      throw new Error("No participants match the selected options.");
    }
    if (action === "print") {
      const ok = printContactRoster({ event, rows });
      if (!ok) {
        throw new Error("Pop-up blocked. Allow pop-ups for this site to print the roster.");
      }
    } else {
      const csv = buildContactRosterCsvExportText(rows);
      downloadCsvTextFromString(buildContactRosterCsvFilename(event?.name), csv);
    }
    setContactRosterOpen(false);
  };

  const handleEdit = () => {
    if (!event?._id) return;
    onClose();
    const returnToWithDetail = (() => {
      const base = (returnTo || buildEventsManagementPath({ tab: EVENT_MGMT_TAB_EVENTS })).trim()
        || buildEventsManagementPath({ tab: EVENT_MGMT_TAB_EVENTS });
      // Only Events page supports the ?detail=... deep-link to reopen this modal.
      if (!base.startsWith("/Events")) return base;
      const join = base.includes("?") ? "&" : "?";
      return `${base}${join}detail=${encodeURIComponent(String(event._id))}`;
    })();
    navigate(`/Events/${event._id}/edit`, { state: { event, eventsFilters, returnTo: returnToWithDetail } });
  };

  const handleRecordKidHoursAndClose = () => {
    if (!event?._id) return;
    onClose();
    navigate(buildEventsManagementPath({ tab: EVENT_MGMT_TAB_CLOSE_RECORD, eventId: event._id }));
  };

  if (!open) return null;

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="event-detail-modal-title"
    >
      <div className="modal-content event-detail-modal" onClick={(e) => e.stopPropagation()}>
        {bannerError && <ErrorBanner message={bannerError} onDismiss={() => setBannerError('')} />}

        <ConfirmationModal
          open={confirmDialog.open}
          title={confirmDialog.title || "Confirm"}
          message={confirmDialog.message}
          mode="confirm"
          onConfirm={() => {
            const fn = confirmDialog.onConfirm;
            setConfirmDialog({ open: false, title: "", message: "", onConfirm: null });
            if (fn) fn();
          }}
          onCancel={() =>
            setConfirmDialog({ open: false, title: "", message: "", onConfirm: null })
          }
        />
        <EventParticipantContactRosterModal
          open={contactRosterOpen}
          participants={participants}
          selectedParticipantIds={selectedParticipantIds}
          onCancel={() => setContactRosterOpen(false)}
          onConfirm={handleContactRosterConfirm}
        />
        <h2 id="event-detail-modal-title" style={{ margin: '0 0 16px 0', fontSize: '1.25rem', color: '#212529' }}>Event Details</h2>

        {eventLoading ? (
          <p>Loading event…</p>
        ) : !event ? (
          <div>
            <p>Event not found.</p>
            <button type="button" onClick={onClose} style={{ padding: '6px 12px', marginTop: '8px' }}>Close</button>
          </div>
        ) : (
          <div style={{ fontSize: '13px', color: '#212529' }}>
            <EventDetailsSummary
              event={event}
              programName={event.program_name}
              organizerName={event.organizer_name}
              kidHoursTracked={eventInCurrentAop}
            />
            {(event.status || 'Open') === 'Closed' && (
              <div style={{ borderTop: '1px solid #e9ecef', paddingTop: '8px', marginTop: '8px' }}>
                <div className="event-detail-closed-sections">
                  <div className="event-detail-closed-sections__col">
                    <div className="event-detail-closed-sections__title">Event Outcome</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {(() => {
                        const at = ['Youth Only', 'Adult Only', 'All Ages'].includes(event.audience_type) ? event.audience_type : 'Youth Only';
                        return (
                          <>
                            {(at === 'Youth Only' || at === 'All Ages') && (
                              <>
                                <div><span style={labelStyle}>Girls participated:</span><span>{event.girls_participated ?? 0}</span></div>
                                <div><span style={labelStyle}>Boys participated:</span><span>{event.boys_participated ?? 0}</span></div>
                              </>
                            )}
                            {(at === 'Adult Only' || at === 'All Ages') && (
                              <>
                                <div><span style={labelStyle}>Women participated:</span><span>{event.women_participated ?? 0}</span></div>
                                <div><span style={labelStyle}>Men participated:</span><span>{event.men_participated ?? 0}</span></div>
                              </>
                            )}
                            <div><span style={labelStyle}>Another participated:</span><span>{event.another_participated ?? 0}</span></div>
                            <div><span style={labelStyle}>Duration hours:</span><span>{event.duration_hours != null ? event.duration_hours : '—'}</span></div>
                            <div><span style={labelStyle}>Kid hours (record):</span><span>{formatKidHours(getEventKidHoursRecord(event))}</span></div>
                            <div><span style={labelStyle}>Kid hours (entered):</span><span>{formatKidHours(getEventKidHoursEntered(event))}</span></div>
                            <div><span style={labelStyle}>Kid hours (total):</span><span>{formatKidHours(getEventKidHoursTotal(event))}</span></div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="event-detail-closed-sections__col">
                    <div className="event-detail-closed-sections__title">Registration Metrics</div>
                    {!participantsLoading ? (
                      <EventRegistrationKpiCards
                        metrics={computeRegistrationMetrics(event, participants)}
                        layout="stack"
                      />
                    ) : null}
                  </div>

                  <div className="event-detail-closed-sections__col">
                    <div className="event-detail-closed-sections__title">Retrospective</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div>
                        <span style={{ fontWeight: 600, color: '#495057', marginRight: '6px', fontSize: '12px' }}>What went well:</span>
                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: '2px' }}>
                          {event.what_went_well ? <LinkifiedText text={event.what_went_well} /> : '—'}
                        </div>
                      </div>
                      <div>
                        <span style={{ fontWeight: 600, color: '#495057', marginRight: '6px', fontSize: '12px' }}>What could improve:</span>
                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: '2px' }}>
                          {event.what_could_improve ? <LinkifiedText text={event.what_could_improve} /> : '—'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <EventDetailsParticipantTabs
              event={event}
              participants={participants}
              loading={participantsLoading}
              tab={tab}
              onTabChange={(t) => { setTab(t); setSelectedParticipantIds(new Set()); }}
              readOnly={!canManage}
              selectedParticipantIds={selectedParticipantIds}
              onSelectAll={handleSelectAll}
              onSelectOne={handleSelectOne}
              onRemoveSelected={removeSelected}
              onRemoveOne={removeOne}
              removing={deleting}
              volunteerOpportunities={volunteerOpportunities}
              volunteerLoading={volunteerLoading}
              showVolunteerTab={canViewVolunteer}
              onOpenVolunteerOpportunity={canViewVolunteer ? handleOpenVolunteerOpportunity : undefined}
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
          <button type="button" onClick={handleBack} style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid #dee2e6', borderRadius: '4px', background: '#fff', color: '#495057', cursor: 'pointer' }}>
            Back
          </button>
          {event && !participantsLoading && participants.length > 0 && (
            <button
              type="button"
              onClick={() => setContactRosterOpen(true)}
              style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid #0d6efd', borderRadius: '4px', background: '#fff', color: '#0d6efd', cursor: 'pointer' }}
              title="Print or export participant roster for this event"
            >
              Print / Export Roster
            </button>
          )}
          {event && canRecordClose && (event.status || 'Open') !== 'Closed' && (
            <button
              type="button"
              onClick={handleRecordKidHoursAndClose}
              style={{ padding: '6px 12px', fontSize: '12px', border: 'none', borderRadius: '4px', background: '#0d6efd', color: '#fff', cursor: 'pointer' }}
              title={eventInCurrentAop ? "Record attendance and close this open event" : "Review attendance and close this open event"}
            >
              {eventInCurrentAop ? "Record Kid Hours & Close" : "Close Event"}
            </button>
          )}
          {event && canViewVolunteer && (
            <button
              type="button"
              onClick={handleCreateVolunteerOpportunity}
              style={{ padding: '6px 12px', fontSize: '12px', border: 'none', borderRadius: '4px', background: '#0d6efd', color: '#fff', cursor: 'pointer' }}
            >
              + Volunteer Opportunity
            </button>
          )}
          {event && canManage && (
            <button
              type="button"
              onClick={handleEdit}
              style={{ padding: '6px 12px', fontSize: '12px', border: 'none', borderRadius: '4px', background: '#0d6efd', color: '#fff', cursor: 'pointer' }}
            >
              Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default EventDetailModal;
